/**
 * SocialNetworkBadge - Componente reutilizável para exibir redes sociais
 * Suporta Instagram, Facebook e outras plataformas
 * Usado em ContactSidebar, CustomerModalSidebar e DealContactCard
 * 
 * @version 1.0.0
 */
import React from 'react';
import { Instagram, Facebook, Copy, ExternalLink, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Tipos de channel_ids suportados
export interface ChannelIds {
  instagram_username?: string;
  instagram_igsid?: string;
  facebook_psid?: string;
  webchat_visitor_id?: string;
  [key: string]: string | undefined;
}

// Props do componente
export interface SocialNetworkBadgeProps {
  /** Objeto com identificadores de canais (instagram_username, facebook_psid, etc.) */
  channelIds?: ChannelIds | null;
  /** Metadados com fallback para username (compatibilidade legada) */
  metadata?: Record<string, unknown> | null;
  /** Variante visual: default (com label) ou compact (só ícone) */
  variant?: 'default' | 'compact';
  /** Exibir ou não o label da rede social */
  showLabel?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

// Configuração das redes sociais suportadas
interface SocialNetworkConfig {
  key: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  getUrl: (username: string) => string;
  getDisplayValue: (value: string) => string;
}

// Mapa de configurações das redes sociais
const SOCIAL_NETWORKS: SocialNetworkConfig[] = [
  {
    key: 'instagram_username',
    name: 'Instagram',
    icon: Instagram,
    colorClass: 'text-pink-500',
    getUrl: (username) => `https://instagram.com/${username}`,
    getDisplayValue: (value) => `@${value}`,
  },
  {
    key: 'facebook_psid',
    name: 'Facebook',
    icon: Facebook,
    colorClass: 'text-blue-600',
    getUrl: () => '#', // Facebook não tem URL direta para PSID
    getDisplayValue: (value) => value.substring(0, 8) + '...',
  },
  {
    key: 'webchat_visitor_id',
    name: 'Webchat',
    icon: Globe,
    colorClass: 'text-emerald-500',
    getUrl: () => '#',
    getDisplayValue: (value) => value.substring(0, 8) + '...',
  },
];

/**
 * Extrai o username do Instagram de channelIds ou metadata
 */
function extractInstagramUsername(
  channelIds?: ChannelIds | null,
  metadata?: Record<string, unknown> | null
): string | null {
  // Prioridade 1: channel_ids.instagram_username
  if (channelIds?.instagram_username) {
    return channelIds.instagram_username;
  }
  
  // Prioridade 2: metadata.username (fallback legado)
  if (metadata?.username && typeof metadata.username === 'string') {
    return metadata.username;
  }
  
  return null;
}

/**
 * Componente que exibe badges de redes sociais com ações de copiar e abrir
 */
export function SocialNetworkBadge({
  channelIds,
  metadata,
  variant = 'default',
  showLabel = true,
  className,
}: SocialNetworkBadgeProps) {
  // Extrai redes sociais disponíveis
  const networks: Array<{ config: SocialNetworkConfig; value: string }> = [];
  
  // Instagram (com fallback para metadata)
  const instagramUsername = extractInstagramUsername(channelIds, metadata);
  if (instagramUsername) {
    const config = SOCIAL_NETWORKS.find(n => n.key === 'instagram_username');
    if (config) {
      networks.push({ config, value: instagramUsername });
    }
  }
  
  // Outras redes (sem fallback, direto do channelIds)
  SOCIAL_NETWORKS.filter(n => n.key !== 'instagram_username').forEach(config => {
    const value = channelIds?.[config.key];
    if (value) {
      networks.push({ config, value });
    }
  });
  
  // Se não houver nenhuma rede, não renderiza nada
  if (networks.length === 0) {
    return null;
  }
  
  // Handler para copiar valor
  const handleCopy = (value: string, networkName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    toast.success(`${networkName} copiado!`);
  };
  
  // Handler para abrir link externo
  const handleOpenExternal = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  
  // Classes base para variantes
  const isCompact = variant === 'compact';
  
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {networks.map(({ config, value }) => {
        const Icon = config.icon;
        const displayValue = config.getDisplayValue(value);
        const externalUrl = config.getUrl(value);
        const hasExternalLink = externalUrl !== '#';
        
        return (
          <div
            key={config.key}
            className={cn(
              'flex items-center gap-2',
              isCompact ? 'py-0' : 'py-0.5'
            )}
          >
            {/* Ícone da rede social */}
            <Icon className={cn('shrink-0', config.colorClass, isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            
            {/* Label da rede (opcional) */}
            {showLabel && !isCompact && (
              <span className="text-xs text-muted-foreground shrink-0">
                {config.name}
              </span>
            )}
            
            {/* Valor e ações */}
            <div className="flex items-center gap-1 ml-auto">
              <span className={cn('font-medium truncate', isCompact ? 'text-[10px]' : 'text-xs')}>
                {displayValue}
              </span>
              
              {/* Botão copiar */}
              <button
                onClick={(e) => handleCopy(value, config.name, e)}
                className="p-0.5 rounded hover:bg-muted transition-colors"
                title={`Copiar ${config.name}`}
              >
                <Copy className={cn('text-muted-foreground hover:text-foreground', isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
              </button>
              
              {/* Botão abrir link externo (se disponível) */}
              {hasExternalLink && (
                <button
                  onClick={(e) => handleOpenExternal(externalUrl, e)}
                  className="p-0.5 rounded hover:bg-muted transition-colors"
                  title={`Abrir perfil no ${config.name}`}
                >
                  <ExternalLink className={cn('text-muted-foreground hover:text-foreground', isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3')} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
